import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createEmployee,
  getEmployeeById,
  updateEmployee,
  getAdminGameSystems,
} from '../../api/EmployeeApi';
import common from '../../common.module.scss';
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

const roles = [
  { value: 'ADMIN', label: 'Администратор' },
  { value: 'MASTER', label: 'Мастер' },
  { value: 'MANAGER', label: 'Менеджер' },
  { value: 'MASTER_MANAGER', label: 'Мастер/Руководитель' },
];

export default function EmployeeForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    role: '',
    login: '',
    password: '',
    gameSystemIds: [], // ✅ для мастеров
  });

  const [loading, setLoading] = useState(true);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [systems, setSystems] = useState([]); // ✅ список систем из админки (пагинация -> content)

  const isMasterRole = useMemo(
    () => form.role === 'MASTER' || form.role === 'MASTER_MANAGER',
    [form.role]
  );

  // ===== load employee (edit)
  useEffect(() => {
    const fetch = async () => {
      if (!isEdit) return;

      try {
        const res = await getEmployeeById(id);
        const emp = res.data;

        setForm({
          name: emp.name || '',
          phoneNumber: emp.phoneNumber || '',
          role: emp.role || '',
          login: emp.login || '',
          password: '',
          gameSystemIds: Array.isArray(emp.gameSystemIds) ? emp.gameSystemIds : [],
        });
      } catch (e) {
        const errorMessage = e?.response?.data?.message || 'Ошибка загрузки сотрудника';
        toast.error(errorMessage);
        console.error('Ошибка загрузки сотрудника', e);
      }
    };

    fetch().finally(() => setLoading(false));
  }, [id, isEdit]);

  // ===== load systems (only when role is master)
  useEffect(() => {
    const loadSystems = async () => {
      if (!isMasterRole) return;

      setSystemsLoading(true);
      try {
        // бэк отдаёт пагинацию {content, page, size...}
        const res = await getAdminGameSystems({ page: 0, size: 200 });
        const list = Array.isArray(res.data?.content) ? res.data.content : [];
        setSystems(list);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || 'Ошибка загрузки систем');
        setSystems([]);
      } finally {
        setSystemsLoading(false);
      }
    };

    loadSystems();
  }, [isMasterRole]);

  // ===== active systems (если надо только активные — включено по умолчанию)
  const activeSystems = useMemo(
    () => systems.filter((s) => s.isActive),
    [systems]
  );

  const allIds = useMemo(
    () => activeSystems.map((s) => s.id),
    [activeSystems]
  );

  const selectedSet = useMemo(
    () => new Set(form.gameSystemIds),
    [form.gameSystemIds]
  );

  const selectedCount = useMemo(
    () => allIds.filter((id) => selectedSet.has(id)).length,
    [allIds, selectedSet]
  );

  const allChecked = useMemo(
    () => allIds.length > 0 && selectedCount === allIds.length,
    [allIds.length, selectedCount]
  );

  const noneChecked = useMemo(
    () => selectedCount === 0,
    [selectedCount]
  );

  const isIndeterminate = useMemo(
    () => !noneChecked && !allChecked,
    [noneChecked, allChecked]
  );

  // ===== ref for indeterminate checkbox
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      // смена роли: если уходим с MASTER/MMASTER — очищаем systems
      if (name === 'role') {
        const nextIsMaster = value === 'MASTER' || value === 'MASTER_MANAGER';
        return {
          ...prev,
          role: value,
          gameSystemIds: nextIsMaster ? prev.gameSystemIds : [],
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const toggleSystem = (systemId) => {
    setForm((prev) => {
      const cur = Array.isArray(prev.gameSystemIds) ? prev.gameSystemIds : [];
      const exists = cur.includes(systemId);
      const nextIds = exists ? cur.filter((id) => id !== systemId) : [...cur, systemId];
      nextIds.sort((a, b) => a - b);
      return { ...prev, gameSystemIds: nextIds };
    });
  };

  const toggleAllSystems = () => {
    setForm((prev) => {
      const nextIds = allChecked ? [] : [...allIds].sort((a, b) => a - b);
      return { ...prev, gameSystemIds: nextIds };
    });
  };

  const handleSubmit = async () => {
    const { name, phoneNumber, role, login, password, gameSystemIds } = form;

    if (!name || !phoneNumber || !role || !login || (!isEdit && !password)) {
      toast.warn('Пожалуйста, заполните все обязательные поля');
      return;
    }

    try {
      if (isEdit) {
        const payload = { name, phoneNumber, role, login };
        if (password) payload.password = password;

        // ✅ отправляем gameSystemIds только для мастерских ролей
        if (role === 'MASTER' || role === 'MASTER_MANAGER') {
          payload.gameSystemIds = Array.isArray(gameSystemIds) ? gameSystemIds : [];
        }

        await updateEmployee(id, payload);
      } else {
        const payload = { name, phoneNumber, role, login, password };

        if (role === 'MASTER' || role === 'MASTER_MANAGER') {
          payload.gameSystemIds = Array.isArray(gameSystemIds) ? gameSystemIds : [];
        }

        await createEmployee(payload);
      }

      navigate('/admin/');
    } catch (e) {
      const errorMessage = e?.response?.data?.message || 'Ошибка при сохранении';
      toast.error(errorMessage);
      console.error('Ошибка при сохранении', e);
    }
  };

  if (loading) return <h2>Загрузка...</h2>;

  return (
    <div className={`container ${common.container}`}>
      <div className={`row justify-content-center align-items-center`}>
        <span className="btn-word black btn-link col-md-auto col-2" onClick={() => navigate(-1)}>
          <FaArrowLeft className={common.serviceButton} />
        </span>
        <h2 className="col-md-auto col-10">
          {isEdit ? 'Редактирование сотрудника' : 'Добавление сотрудника'}
        </h2>
      </div>

      <div className="row mt-4">
        <div className="col-md-6 offset-md-3">
          <input
            className="form-control mb-3"
            placeholder="Имя и фамилия"
            name="name"
            value={form.name}
            onChange={handleChange}
          />

          <input
            className="form-control mb-3"
            placeholder="Телефон"
            name="phoneNumber"
            value={form.phoneNumber}
            onChange={handleChange}
          />

          <select
            className="form-control mb-3"
            name="role"
            value={form.role}
            onChange={handleChange}
          >
            <option value="">Выберите роль</option>
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          {/* ✅ Блок систем для MASTER / MASTER_MANAGER */}
          {isMasterRole && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: 600 }}>Системы, которые ведёт мастер</div>

                <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAllSystems}
                    disabled={systemsLoading || allIds.length === 0}
                  />
                  <span style={{ userSelect: 'none' }}>Выбрать все</span>
                </label>
              </div>

              <div
                className="form-control"
                style={{
                  height: 'auto',
                  maxHeight: 260,
                  overflowY: 'auto',
                  padding: 12,
                }}
              >
                {systemsLoading && <div>Загрузка систем...</div>}

                {!systemsLoading && activeSystems.length === 0 && (
                  <div style={{ opacity: 0.7 }}>Нет доступных систем</div>
                )}

                {!systemsLoading &&
                  activeSystems.map((s) => {
                    const checked = form.gameSystemIds.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className="d-flex align-items-center gap-2 mb-2"
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSystem(s.id)}
                        />
                        <span>{s.name}</span>
                      </label>
                    );
                  })}
              </div>

              <div style={{ marginTop: 8, opacity: 0.8 }}>
                Выбрано систем: {selectedCount} из {allIds.length}
              </div>
            </div>
          )}

          <input
            className="form-control mb-3"
            placeholder="Логин"
            name="login"
            value={form.login}
            onChange={handleChange}
          />

          <input
            className="form-control mb-3"
            placeholder="Пароль"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
          />

          <button className="service-btn w-100" onClick={handleSubmit}>
            Сохранить сотрудника
          </button>
        </div>
      </div>
    </div>
  );
}